import { useCallback, useEffect, useRef } from 'react';

import { AuthUser as User } from '../types/auth.types';
import type { MeResponse } from '../types/auth.types';
import {
  clearSocketAuth,
  connectSocket,
  disconnectSocket,
  registerSessionRevokedHandler,
  setSocketAuth
} from '../../../lib/socket';
import { ApiError, clearBrowserSessionMarkers, getStoredAccessToken, registerAuthFailureHandler, setStoredAccessToken } from '../../../lib/api';
import { getErrorMessage } from '../../../lib/errorMessage';
import {
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush
} from '../services/pushService';
import { authApi } from '../api/auth.api';
import {
  clearNativeAuthTokens,
  isNativeCapacitorClient,
  persistNativeAuthTokens,
  readNativeRefreshToken
} from '../../../lib/nativeAuth';
import { normalizeAuthUserRecord, normalizeSessionUser } from './session.normalizer';
import type { AuthLoginResponse } from '../types/auth.types';
import { normalizeRole } from '../rbac';
import { persistCart } from '../../../lib/cartStorage';
import { recordClientDiagnostic } from '../../../lib/clientDiagnostics';

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface useSessionHooksParams {
  setCurrentUser: (user: User | null) => void;
  addToast: (message: string, type?: ToastType) => void;
  currentUser?: User | null;
}

/** Serialize restore/login so in-flight refresh cannot race with a new sign-in. */
let sessionOperation: Promise<unknown> = Promise.resolve();

const runSessionOperation = async <T>(operation: () => Promise<T>): Promise<T> => {
  const next = sessionOperation.then(operation, operation);
  sessionOperation = next.then(
    () => undefined,
    () => undefined
  );
  return next;
};

const hasRefreshCookie = () => {
  if (typeof document === 'undefined') return false;
  return /(?:^|; )has_session=/.test(document.cookie);
};

export const useSessionHooks = ({ setCurrentUser, addToast, currentUser }: useSessionHooksParams) => {
  const currentUserRef = useRef<User | null>(null);

  useEffect(() => {
    currentUserRef.current = currentUser || null;
  }, [currentUser]);

  useEffect(() => {
    registerAuthFailureHandler((error: ApiError) => {
      if (error.status === 403) {
        // 403 Forbidden is a route/permission denial, NOT a session invalidation.
        // Never wipe tokens, cart, socket, or currentUser on 403.
        addToast(getErrorMessage(error, 'ACCESS DENIED'), 'warning');
        return;
      }

      const hadSession = Boolean(currentUserRef.current || hasRefreshCookie() || getStoredAccessToken());
      disconnectSocket();
      clearSocketAuth();
      clearNativeAuthTokens();
      setStoredAccessToken(null);
      setCurrentUser(null);
      if (error.status === 401) {
        const msg = error.message?.toLowerCase() || '';
        const isRoutingMismatch = msg.includes('multi-instance') || msg.includes('routing mismatch');

        if (hadSession && !isRoutingMismatch) {
          addToast(getErrorMessage(error, 'Your session expired or changed. Please sign in again.'), 'warning');
        }
      }
    });

    registerSessionRevokedHandler(payload => {
      const hadSession = Boolean(currentUserRef.current || hasRefreshCookie() || getStoredAccessToken());
      disconnectSocket();
      clearSocketAuth();
      clearNativeAuthTokens();
      setStoredAccessToken(null);
      setCurrentUser(null);
      if (hadSession) {
        const reason = payload?.source === 'refresh_reuse'
          ? 'SESSION REVOKED FOR SECURITY. PLEASE SIGN IN AGAIN.'
          : 'SESSION REVOKED. PLEASE SIGN IN AGAIN.';
        addToast(reason, 'warning');
      }
    });

    return () => {
      registerAuthFailureHandler(null);
      registerSessionRevokedHandler(null);
    };
  }, [addToast, setCurrentUser]);

  // Silent background token renewal: refresh access token every 10 minutes (well before the 15m JWT expiration)
  useEffect(() => {
    if (!currentUser) return;

    const intervalId = window.setInterval(async () => {
      try {
        const nativeRefresh = isNativeCapacitorClient() ? readNativeRefreshToken() : undefined;
        const refreshed = nativeRefresh
          ? await authApi.refreshWithToken(nativeRefresh).catch(() => null)
          : await authApi.refresh().catch(() => null);

        if (refreshed?.success && refreshed?.data?.accessToken) {
          setStoredAccessToken(refreshed.data.accessToken);
          if (nativeRefresh) {
            persistNativeAuthTokens(
              refreshed.data.accessToken,
              refreshed.data.refreshToken || nativeRefresh
            );
          }
        }
      } catch {
        // Next silent background interval will retry
      }
    }, 10 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentUser]);

  /** Lightweight profile refresh (e.g. after wallet top-up) without full token/socket churn. */
  const refreshSessionUser = useCallback(async (): Promise<boolean> => {
    try {
      const payload: MeResponse = await authApi.me().catch(() => ({ success: false } as MeResponse));
      if (!payload?.success) return false;
      const mapped = normalizeSessionUser(payload);
      if (!mapped) return false;
      setCurrentUser(mapped as User);
      return true;
    } catch {
      return false;
    }
  }, [setCurrentUser]);

  const bootstrapSocket = useCallback(
    (mapped: User, sessionId?: string, accessToken?: string) => {
      const userId = String(mapped._id || mapped.id || '').trim();
      if (!userId) return;

      const normalizedRole = normalizeRole(String(mapped.role || 'customer'));
      setSocketAuth({
        accessToken: accessToken?.trim() || undefined,
        sessionId: sessionId?.trim() || undefined,
        role: normalizedRole,
        userId
      });

      Promise.resolve().then(() => {
        connectSocket().catch(e => recordClientDiagnostic('[Socket] Connection failed', { error: String(e) }, 'warning'));
      });

      registerServiceWorker()
        .then(() => {
          requestNotificationPermission().then(granted => {
            if (granted) {
              subscribeToPush().catch(e => recordClientDiagnostic('[Push] Subscription failed', { error: String(e) }, 'warning'));
            }
          });
        })
        .catch(e => recordClientDiagnostic('[SW] Registration failed', { error: String(e) }, 'warning'));
    },
    []
  );

  /**
   * Apply user from login response. POST /auth/login already issued cookies/tokens
   * and bound the Express session — do not rotate again with /auth/refresh.
   */
  const applyLoginSession = useCallback(
    async (loginResponse: AuthLoginResponse): Promise<boolean> =>
      runSessionOperation(async () => {
        const mapped = normalizeAuthUserRecord(loginResponse?.data?.user);
        if (!mapped) {
          addToast('Login response missing user profile.', 'error');
          return false;
        }

        setStoredAccessToken(loginResponse.data?.accessToken);
        setCurrentUser(mapped as User);
        if (isNativeCapacitorClient()) {
          persistNativeAuthTokens(loginResponse.data?.accessToken, loginResponse.data?.refreshToken);
        }
        bootstrapSocket(
          mapped as User,
          loginResponse.data?.sessionId,
          loginResponse.data?.accessToken
        );

        const displayName = mapped.username || mapped.name || 'user';
        addToast(`Signed in as ${displayName}`, 'success');
        return true;
      }),
    [addToast, bootstrapSocket, setCurrentUser]
  );

  const restoreSession = useCallback(
    async () =>
      runSessionOperation(async () => {
        try {
          const nativeRefresh = isNativeCapacitorClient() ? readNativeRefreshToken() : undefined;
          const storedToken = getStoredAccessToken();
          const hadSession = Boolean(nativeRefresh || hasRefreshCookie() || storedToken);
          if (!hadSession) {
            setCurrentUser(null);
            return false;
          }
          const refreshed = nativeRefresh
            ? await authApi.refreshWithToken(nativeRefresh).catch(() => null)
            : await authApi.refresh().catch(() => null);
          if (!refreshed?.success) {
            if (!nativeRefresh) {
              clearBrowserSessionMarkers();
            }
            disconnectSocket();
            clearSocketAuth();
            setCurrentUser(null);
            if (hadSession) {
              addToast('Your session expired or changed. Please sign in again.', 'warning');
            }
            return false;
          }
          if (refreshed?.data?.accessToken) {
            setStoredAccessToken(refreshed.data.accessToken);
          }
          if (nativeRefresh && refreshed?.data?.accessToken) {
            persistNativeAuthTokens(
              refreshed.data.accessToken,
              refreshed.data.refreshToken || nativeRefresh
            );
          }
          const payload: MeResponse = await authApi.me().catch(() => ({ success: false } as MeResponse));
          if (!payload?.success) {
            setCurrentUser(null);
            return false;
          }

          const mapped = normalizeSessionUser(payload);
          if (!mapped) {
            setCurrentUser(null);
            return false;
          }

          setCurrentUser(mapped as User);

          const accessToken = (() => {
            const refreshedToken =
              refreshed?.data && 'accessToken' in refreshed.data
                ? String(refreshed.data.accessToken || '')
                : '';
            return refreshedToken.trim() || undefined;
          })();
          const sessionId =
            refreshed?.data?.sessionId ||
            (payload.data && 'sessionId' in payload.data ? String(payload.data.sessionId || '') : '') ||
            undefined;
          bootstrapSocket(mapped as User, sessionId, accessToken);

          return true;
        } catch {
          disconnectSocket();
          clearSocketAuth();
          setCurrentUser(null);
          return false;
        }
      }),
    [addToast, bootstrapSocket, setCurrentUser]
  );

  const logout = useCallback(async () => {
    await runSessionOperation(async () => {
      try {
        await unsubscribeFromPush().catch(() => false);
        await authApi.logout();
      } catch {
        // ignore
      } finally {
        disconnectSocket();
        clearSocketAuth();
        clearNativeAuthTokens();
        /**
         * Clear ONLY the local cart cache on logout — never the server cart.
         * If a customer signs back in (here or on another device) their server
         * cart should still hold whatever they left behind. We also strip the
         * local copy so a different user signing in on a shared device does
         * not inherit the prior session's items before the server cart loads.
         */
        try {
          persistCart([]);
        } catch {
          // ignore storage failures (e.g. private mode)
        }
        setCurrentUser(null);
        addToast('SIGNED OUT', 'info');
      }
    });
  }, [addToast, setCurrentUser]);

  const sendPasswordResetLink = useCallback(
    async (userId: string) => {
      try {
        const response = await authApi.sendResetLink(userId);
        const apiError = (response as { error?: string } | null)?.error;
        if (apiError) throw new Error(apiError);
        const message = response?.data?.message || response?.message || 'Password reset link sent.';
        addToast(message, 'success');
        return true;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to send reset link.';
        addToast(message, 'warning');
        return false;
      }
    },
    [addToast]
  );

  return { applyLoginSession, restoreSession, refreshSessionUser, logout, sendPasswordResetLink };
};
